// Left panel: Ledgerate AI. Build mode turns a request into steps (shown as
// a draft the user accepts or discards); Ask mode answers questions about the
// workflow. Before the first message it offers context-aware next steps.
// Roadmap 2: the steps are AI-written code (useCodeChat, CodeDraftCard);
// the Roadmap 1 step chips and step-tool draft card are commented out below.
//
// Files are never added here: the chat takes plain English only (no files,
// pasted images or links). Sources are added with the Source button on the
// trail. Before the first message, every source's header row is confirmed,
// since its column names are the only text from a file the AI sees.
import { useEffect, useRef, useState } from "react";
import { Button, InputNumber, Spin, Tooltip } from "antd"; // Roadmap 1 draft card also used Input; the Build/Ask switch used Segmented
import { AlertTriangle, ArrowUp, EyeOff, FileOutput, Play, RotateCcw, Square, Wand2, X } from "lucide-react";
// import { describeStep } from "../../../engine"; // Roadmap 1 draft card
import { getStepDefinition } from "../../../engine";
// import { useAiChat } from "../ai/useAiChat"; // Roadmap 1: built-in step tools
import { useCodeChat } from "../ai/useCodeChat";
import { needsHeaderCheck } from "../ai/codeShape";
import { columnNameProblem, splitPlaceholders } from "../ai/promptMask";
import { loadTextRules } from "../ai/textRules";
import { reshapeSource } from "../shared/sourceFiles";
import { useBuilder } from "./BuilderContext";
import CodeDraftCard from "./CodeDraftCard";

// Roadmap 1 offered built-in steps as starters:
// const STARTER_STEPS = [
//   ["filterRows", "Filter rows"],
//   ["groupBy", "Group & total"],
//   ["changeType", "Fix number/date types"],
//   ["selectColumns", "Choose columns"],
// ];
// Roadmap 2 offers example requests instead; clicking one fills the message box.
const EXAMPLE_REQUESTS = [
  "Keep only the rows where column A has a real date",
  "Remove duplicate rows",
  "Total the amounts per customer",
  "Split full names into first and last name",
];

const Chip = ({ icon: Icon, children, onClick }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:border-teal-600 hover:text-teal-700">
    {Icon && <Icon size={12} />}
    {children}
  </button>
);

const Bubble = ({ children }) => <div className="text-[13px] leading-relaxed text-gray-800">{children}</div>;

/* Picked files for a new source here before the chat became text-only:
const FilePicker = ({ onFiles, children }) => (
  <Upload multiple accept=".csv,.xlsx,.txt" showUploadList={false} beforeUpload={(file, list) => { if (file === list[0]) onFiles(list); return false; }}>
    {children}
  </Upload>
); */

// Text with placeholders: the hidden values are shown (they never left the
// device) and marked as hidden from the AI.
const MaskedText = ({ text, vault }) =>
  splitPlaceholders(text, vault).map((part, index) =>
    part.token ? (
      <Tooltip key={index} title="Hidden from Ledgerate AI: it only saw a placeholder">
        <span className="rounded bg-teal-50 px-1 text-teal-800">{String(part.value)}</span>
      </Tooltip>
    ) : (
      <span key={index}>{part.text}</span>
    ),
  );

// What the AI received for a message, with placeholders as chips. Hidden
// values always stay hidden: there is no way to show one to the AI.
const SentAs = ({ sent }) => (
  <div className="mt-1 flex items-start gap-1 text-[11px] text-gray-500">
    <EyeOff size={11} className="mt-0.5 shrink-0" />
    <span className="min-w-0 [overflow-wrap:anywhere]">
      Sent to Ledgerate AI as:{" "}
      {sent.split(/(\{\{(?:text|number|date)_\d+\}\})/).map((part, index) =>
        /^\{\{/.test(part) ? (
          <span key={index} className="rounded bg-gray-200 px-1 font-mono text-[10px] text-gray-700">
            {part.slice(2, -2)}
          </span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  </div>
);

const STAGES = {
  sending: "Sending…",
  thinking: "Thinking…",
  writing: "Writing…",
  retrying: "Checking its answer and correcting it…",
  testing: "Testing on your sample…",
};

// While Ledgerate AI works: its stage, the message it is writing and the
// step it has reached.
const Progress = ({ progress, vault, onStop }) => (
  <div className="space-y-1">
    {progress?.message && (
      <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-800 [overflow-wrap:anywhere]">
        <MaskedText text={progress.message} vault={vault} />
      </div>
    )}
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <Spin size="small" />
      <span>{progress?.step ? `Writing step ${progress.step}…` : STAGES[progress?.stage] || "Working on it…"}</span>
      {progress && progress.stage !== "testing" && (
        <button type="button" onClick={onStop} className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
          <Square size={10} /> Stop
        </button>
      )}
    </div>
  </div>
);

// Asks the user to confirm a source's header row before its column names are
// shared with the AI, or to use column letters instead.
const HeaderCheck = ({ source, file, onDone }) => {
  const { dispatch } = useBuilder();
  const [rules, setRules] = useState(null);
  const [rulesFailed, setRulesFailed] = useState(false);
  const [row, setRow] = useState(source.headerRow || 1);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState(null);
  // The column names are checked with the server's rules; without them they can't be confirmed.
  const fetchRules = () => {
    setRulesFailed(false);
    loadTextRules().then(setRules, () => setRulesFailed(true));
  };
  useEffect(fetchRules, []);
  const flagged = rules ? source.columns.filter((c) => columnNameProblem(rules, c.name)) : [];
  const currentRow = source.headerRow || 1;
  const useRow = () => {
    setReading(true);
    setReadError(null);
    reshapeSource(dispatch, source, file, { hasHeader: true, headerRow: row }).catch((error) => {
      setReading(false);
      setReadError(`Could not read row ${row}: ${error.message}`);
    });
  };
  const confirm = () => {
    dispatch({ type: "updateSource", id: source.id, changes: { headerConfirmed: true } });
    onDone?.();
  };
  const useLetters = () => {
    if (file) reshapeSource(dispatch, source, file, { hasHeader: false, matchBy: "position" });
    else
      dispatch({
        type: "updateSource",
        id: source.id,
        changes: { hasHeader: false, matchBy: "position", columns: source.columns.map((c) => ({ ...c, name: c.letter, header: null })) },
      });
  };
  return (
    <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3 text-xs text-gray-700">
      <div className="font-semibold text-gray-900">Check the header row of {source.label || source.id}</div>
      <p>
        Ledgerate AI only sees column names, never your rows. Row {source.headerRow || 1} is used as the header, so these names would be shared:
      </p>
      <div className="flex max-h-28 flex-wrap gap-1 overflow-auto">
        {source.columns.map((c) => (
          <span key={c.letter} className={`rounded px-1.5 py-0.5 ${flagged.includes(c) ? "bg-red-100 text-red-800" : "bg-white text-gray-800 ring-1 ring-gray-200"}`}>
            {c.letter}: {c.name}
          </span>
        ))}
      </div>
      {flagged.length > 0 && (
        <p className="flex items-start gap-1 text-red-700">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          The names in red look like data (an email, phone number, long number, link or instruction), not column names. Pick the right header row, or use column letters.
        </p>
      )}
      {!rules && !rulesFailed && (
        <p className="flex items-center gap-1.5 text-gray-500">
          <Spin size="small" /> Checking the column names…
        </p>
      )}
      {rulesFailed && (
        <p className="flex items-center gap-1 text-red-700">
          <AlertTriangle size={12} className="shrink-0" />
          Couldn't check the column names (no connection to Ledgerate).
          <button type="button" onClick={fetchRules} className="font-medium underline hover:text-red-900">
            Retry
          </button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="small" type="primary" disabled={!rules || flagged.length > 0} onClick={confirm}>
          Yes, these are column names
        </Button>
        <Button size="small" onClick={useLetters}>
          No header row (use A, B, C…)
        </Button>
      </div>
      {file && (
        <div className="flex items-center gap-1.5">
          <span>Header is on row</span>
          <InputNumber size="small" min={1} value={row} onChange={(value) => value && setRow(value)} className="w-16" />
          <Tooltip title={row === currentRow ? `Row ${row} is already the header` : ""}>
            <Button size="small" loading={reading} disabled={row === currentRow} onClick={useRow}>
              Use this row
            </Button>
          </Tooltip>
        </div>
      )}
      {readError && <p className="text-red-700">{readError}</p>}
    </div>
  );
};

// Sources without a header row: their columns are named by letter.
const LettersHint = ({ sources }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
    {sources.map((s) => s.label || s.id).join(", ")} {sources.length > 1 ? "have" : "has"} no header row, so columns are named by letter. Refer to them as
    "column A", "column B"… You can set a header row in the source settings.
  </div>
);

const Suggestions = ({ onRun, onExample }) => {
  const { state, dispatch } = useBuilder();
  const { workflow } = state;

  if (!workflow.sources.length) {
    return (
      <>
        <Bubble>
          Hi! Add the file(s) you want to transform with the <strong>Source</strong> button above the trail, then describe what you need. Your files stay on your
          device; I only see column names.
        </Bubble>
      </>
    );
  }
  if (!workflow.steps.length) {
    return (
      <>
        <Bubble>
          You've added <strong>{workflow.sources.length}</strong> source{workflow.sources.length > 1 ? "s" : ""}. What do you want to do with {workflow.sources.length > 1 ? "them" : "it"}?
        </Bubble>
        {/* Roadmap 1:
        <div className="flex flex-wrap gap-1.5">
          {STARTER_STEPS.map(([type, label]) => (
            <Chip key={type} icon={Wand2} onClick={() => addStep(type)}>
              {label}
            </Chip>
          ))}
          {workflow.sources.length > 1 && (
            <Chip icon={Wand2} onClick={() => addStep("join")}>
              Join Source 1 + Source 2
            </Chip>
          )}
          <Chip icon={Plus} onClick={() => openAddStep({})}>
            All steps…
          </Chip>
        </div> */}
        <Bubble>Describe it in your own words and I'll write the steps. They run on your device; I only see column names.</Bubble>
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLE_REQUESTS.map((example) => (
            <Chip key={example} icon={Wand2} onClick={() => onExample(example)}>
              {example}
            </Chip>
          ))}
        </div>
      </>
    );
  }
  if (!workflow.outputs.length) {
    return (
      <>
        <Bubble>Looking good. Send the result to a file when you're ready.</Bubble>
        <div className="flex flex-wrap gap-1.5">
          <Chip icon={FileOutput} onClick={() => dispatch({ type: "addOutput" })}>
            Add an output file
          </Chip>
          {/* Roadmap 1: <Chip icon={Plus} onClick={() => openAddStep({})}>Add another step</Chip> */}
        </div>
      </>
    );
  }
  return (
    <>
      <Bubble>This workflow looks complete. Run it on next month's files, or keep refining it.</Bubble>
      <div className="flex flex-wrap gap-1.5">
        <Chip icon={Play} onClick={onRun}>
          Run on new files
        </Chip>
        {/* Roadmap 1: <Chip icon={Plus} onClick={() => openAddStep({})}>Add a step</Chip> */}
      </div>
    </>
  );
};

/* Roadmap 1: draft card for built-in step tools (replaced by CodeDraftCard).
const stepName = (step) => step.label || getStepDefinition(step.type)?.label || step.type;

// What the draft changes compared with the current workflow.
const draftChanges = (current, proposed) => {
  const before = new Map(current.steps.map((s) => [s.id, JSON.stringify([s.type, s.inputs, s.params])]));
  const steps = proposed.steps.map((step) => ({
    step,
    change: !before.has(step.id) ? "new" : before.get(step.id) !== JSON.stringify([step.type, step.inputs, step.params]) ? "changed" : null,
  }));
  const removed = current.steps.filter((s) => !proposed.steps.some((p) => p.id === s.id));
  const wrote = new Map(current.outputs.map((o) => [o.id, JSON.stringify(o.sheets)]));
  const outputs = proposed.outputs
    .filter((o) => wrote.get(o.id) !== JSON.stringify(o.sheets))
    .map((output) => ({ output, change: wrote.has(output.id) ? "changed" : "new" }));
  return { steps, removed, outputs };
};

const CHANGE_STYLES = { new: "bg-teal-50 text-teal-800", changed: "bg-amber-50 text-amber-800" };

// The AI's proposal: what changes, the data values to fill in, accept/discard.
const DraftCard = ({ draft, onAccept, onDiscard }) => {
  const { state } = useBuilder();
  const [values, setValues] = useState({});
  const { steps, removed, outputs } = draftChanges(state.workflow, draft.workflow);
  const missing = draft.placeholders.filter((name) => !String(values[name] ?? "").trim());

  return (
    <div className="space-y-2 rounded-xl border border-teal-200 bg-white p-3">
      <div className="text-xs font-semibold text-gray-900">Proposed changes</div>
      <ol className="space-y-1">
        {steps.map(({ step, change }, index) => (
          <li key={step.id} className="flex items-start gap-1.5 text-xs text-gray-700">
            <span className="w-4 shrink-0 text-gray-400">{index + 1}.</span>
            <span className="min-w-0 flex-1">
              <span className="font-medium">{stepName(step)}</span>
              <span className="block truncate text-gray-500">{describeStep(step)}</span>
            </span>
            {change && <span className={`rounded px-1 text-[10px] ${CHANGE_STYLES[change]}`}>{change}</span>}
          </li>
        ))}
        {removed.map((step) => (
          <li key={step.id} className="text-xs text-red-700 line-through">
            {stepName(step)}
          </li>
        ))}
        {outputs.map(({ output, change }) => (
          <li key={output.id} className="text-xs text-teal-800">
            {change === "new" ? "+ Output" : "Output now writes the last step"}: {output.fileName}.{output.format}
          </li>
        ))}
      </ol>
      {draft.placeholders.length > 0 && (
        <div className="space-y-1.5 border-t border-gray-100 pt-2">
          <div className="text-[11px] text-gray-500">Fill in the values. They stay in your workflow and are never sent to the AI.</div>
          {draft.placeholders.map((name) => (
            <label key={name} className="block text-[11px] text-gray-600">
              {name.replace(/_/g, " ")}
              <Input size="small" value={values[name] ?? ""} onChange={(e) => setValues({ ...values, [name]: e.target.value })} />
            </label>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-1.5">
        <Button size="small" onClick={onDiscard}>
          Discard
        </Button>
        <Tooltip title={missing.length ? "Fill in every value first" : ""}>
          <Button size="small" type="primary" disabled={missing.length > 0} onClick={() => onAccept(values)}>
            Accept
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
*/

// The panel's width: dragged by its right edge, remembered in this browser,
// reset by double-clicking the edge.
const WIDTH_KEY = "ledgerate.aiPanelWidth";
const MIN_WIDTH = 300;
const maxWidth = () => Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.6));
const clampWidth = (width) => Math.min(maxWidth(), Math.max(MIN_WIDTH, Math.round(width)));

const usePanelWidth = () => {
  const [width, setWidth] = useState(() => clampWidth(Number(localStorage.getItem(WIDTH_KEY)) || MIN_WIDTH));
  useEffect(() => {
    localStorage.setItem(WIDTH_KEY, String(width));
  }, [width]);
  useEffect(() => {
    const onResize = () => setWidth((current) => clampWidth(current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const startDrag = (event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const onMove = (e) => setWidth(clampWidth(startWidth + e.clientX - startX));
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  return { width, startDrag, reset: () => setWidth(MIN_WIDTH) };
};

const AiPanel = ({ onRun }) => {
  const panel = usePanelWidth();
  const { state, dispatch, talkingAbout: pinnedId, setTalkingAbout, aiConversation } = useBuilder();
  // The Build/Ask switch is off for now: every message is in Build mode, where
  // Claude also answers questions (without changing steps).
  // const [mode, setMode] = useState("build");
  const mode = "build";
  const [dismissed, setDismissed] = useState(null);
  const [text, setText] = useState("");
  // const chat = useAiChat({ workflow: state.workflow, files: state.files, conversation: aiConversation }); // Roadmap 1
  const chat = useCodeChat({ workflow: state.workflow, files: state.files, conversation: aiConversation });
  const bottom = useRef(null);
  // The step the conversation is about: one pinned via "Ask AI", else the selected step.
  const stepId = pinnedId ?? state.selectedId;
  const step = state.workflow.steps.find((s) => s.id === stepId);
  const talkingAbout = step && dismissed !== step.id ? step : null;
  const unchecked = state.workflow.sources.filter(needsHeaderCheck);
  const letterSources = state.workflow.sources.filter((s) => !s.hasHeader);
  const [pending, setPending] = useState(null); // a message waiting for header checks
  const [notice, setNotice] = useState(null);

  // Braces matter: scrollIntoView may return a Promise, which React would call as a cleanup.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [chat.items.length, chat.draft, chat.busy, chat.progress]);

  const submit = () => {
    if (!text.trim() || chat.busy) return;
    setNotice(null);
    if (unchecked.length) {
      setPending({ text, focusStepId: talkingAbout?.id });
      return;
    }
    chat.send(text, { mode, focusStepId: talkingAbout?.id });
    setText("");
  };

  // Sends the waiting message once every header row is checked.
  useEffect(() => {
    if (!pending || unchecked.length || chat.busy) return;
    chat.send(pending.text, { mode, focusStepId: pending.focusStepId });
    setPending(null);
    setText("");
  }, [pending, unchecked.length, chat, mode]);

  // The message box takes typed or pasted text only: no files or images.
  const refuseFiles = (event) => {
    const transfer = event.dataTransfer || event.clipboardData;
    if (!transfer || (!transfer.files?.length && ![...(transfer.types || [])].includes("Files"))) return;
    event.preventDefault();
    setNotice("Files can't be added to a message. Add them with the Source button above the trail.");
  };

  const accept = () => dispatch({ type: "applyDraft", ...chat.accept() });

  return (
    <aside
      className="relative flex shrink-0 flex-col border-r border-gray-200 bg-white"
      style={{ width: panel.width }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={refuseFiles}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the AI panel"
        title="Drag to resize. Double-click to reset."
        onPointerDown={panel.startDrag}
        onDoubleClick={panel.reset}
        className="absolute -right-1 top-0 z-20 h-full w-2 cursor-col-resize hover:bg-teal-500/30 active:bg-teal-500/40"
      />
      <div className="flex h-12 items-center justify-between border-b border-gray-100 px-4">
        <span className="text-sm font-semibold text-gray-900">Build your workflow</span>
        {/* Clears the chat and starts a fresh conversation with the AI (the workflow itself is kept). */}
        {chat.items.length > 0 && (
          <Tooltip title="Clear this chat and start a fresh one. Your workflow stays as it is.">
            <button
              type="button"
              onClick={chat.reset}
              disabled={chat.busy}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40"
            >
              <RotateCcw size={12} /> Start over
            </button>
          </Tooltip>
        )}
        {/* Build/Ask switch, off for now:
        <Segmented size="small" value={mode} onChange={setMode} options={[{ value: "build", label: "Build" }, { value: "ask", label: "Ask" }]} /> */}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {!chat.items.length && <Suggestions onRun={onRun} onExample={setText} />}
        {chat.items.map((item, index) =>
          item.role === "user" ? (
            <div key={index} className="ml-6">
              <div className="whitespace-pre-wrap rounded-xl bg-gray-100 px-3 py-2 text-[13px] text-gray-900 [overflow-wrap:anywhere]">{item.text}</div>
              {item.sent && item.sent !== item.text && <SentAs sent={item.sent} />}
            </div>
          ) : (
            <div
              key={index}
              className={`whitespace-pre-wrap leading-relaxed [overflow-wrap:anywhere] ${item.role === "error" ? "text-[13px] text-red-700" : item.role === "status" ? "text-xs italic text-gray-500" : "text-[13px] text-gray-800"}`}
            >
              {item.role === "assistant" ? <MaskedText text={item.text} vault={chat.vault} /> : item.text}
            </div>
          ),
        )}
        {pending && unchecked[0] && (
          <HeaderCheck key={`${unchecked[0].id}:${unchecked[0].headerRow}:${unchecked[0].hasHeader}`} source={unchecked[0]} file={state.files[unchecked[0].id]} />
        )}
        {pending && unchecked.length > 0 && (
          <button type="button" onClick={() => setPending(null)} className="text-[11px] text-gray-500 underline hover:text-gray-800">
            Cancel this message
          </button>
        )}
        {chat.busy && <Progress progress={chat.progress} vault={chat.vault} onStop={chat.cancel} />}
        {/* Roadmap 1: <DraftCard key={chat.items.length} draft={chat.draft} onAccept={accept} onDiscard={chat.discard} /> */}
        {chat.draft && !chat.busy && <CodeDraftCard key={chat.items.length} draft={chat.draft} onValue={chat.setValue} onAccept={accept} onDiscard={chat.discard} />}
        <div ref={bottom} />
      </div>

      <div className="space-y-2 border-t border-gray-100 p-3">
        {talkingAbout && (
          <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[11px] text-teal-800">
            Talking about: {talkingAbout.label || getStepDefinition(talkingAbout.type)?.label}
            <button
              type="button"
              onClick={() => {
                setDismissed(talkingAbout.id);
                setTalkingAbout(null);
              }}
              className="hover:text-teal-950"
            >
              <X size={11} />
            </button>
          </span>
        )}
        {letterSources.length > 0 && <LettersHint sources={letterSources} />}
        {notice && <div className="text-[11px] text-red-700">{notice}</div>}
        <div className="rounded-xl border border-gray-300 bg-white p-2 focus-within:border-teal-600">
          <textarea
            rows={3}
            value={text}
            maxLength={2000}
            disabled={chat.busy || Boolean(pending)}
            onChange={(e) => setText(e.target.value)}
            onPaste={refuseFiles}
            onDrop={refuseFiles}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={
              mode === "build"
                ? "Describe what you need, e.g. keep only the rows where column A has a real date."
                : "Ask about this workflow or step…"
            }
            className="w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-gray-400"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400">Plain English only. Names, numbers and dates are hidden from the AI.</span>
            <button
              type="button"
              onClick={submit}
              disabled={chat.busy || !text.trim()}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-white disabled:opacity-40"
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AiPanel;
